import { Text, Icon, Colors } from '@amelio/ui-library';
import { useEffect, useState } from 'react';

import {
    CompanyValueCard,
    type CompanyValueCardProps,
} from '../../../shared/ManagerAssessment/CompanyValueCard';
import { useManagerAssessmentSummary } from '../hooks/useManagerAssessmentSummary';

import { Skeleton, Bone } from '@/components/Skeleton/Skeleton';
import { useAmelioTranslations } from '@/hooks';

interface SummaryTabProps {
    cycleInstanceId?: string;
    companyValueCards?: CompanyValueCardProps[];
    className?: string;
    dataTestId?: string;
}

interface SummaryEmptyStateProps {
    variant: 'strength' | 'development';
    title: string;
    description: string;
}

interface SummarySectionProps {
    title: string;
    items: { title: string; description: string }[];
    dotColor: string;
    /** Shown instead of the list when the selection left this section empty (a legitimate outcome). */
    emptyState: SummaryEmptyStateProps;
}

const CompanyValuesCarousel = ({
    companyValueCards,
}: {
    companyValueCards: CompanyValueCardProps[];
}) => {
    const { t } = useAmelioTranslations();
    const [currentSlide, setCurrentSlide] = useState(0);
    const [itemsPerSlide, setItemsPerSlide] = useState(4);

    useEffect(() => {
        const updateItemsPerSlide = () => {
            if (window.innerWidth < 640) {
                setItemsPerSlide(1);
            } else if (window.innerWidth < 1024) {
                setItemsPerSlide(2);
            } else {
                setItemsPerSlide(4);
            }
        };
        updateItemsPerSlide();
        window.addEventListener('resize', updateItemsPerSlide);
        return () => window.removeEventListener('resize', updateItemsPerSlide);
    }, []);

    useEffect(() => {
        const totalSlides = Math.ceil(companyValueCards.length / itemsPerSlide);
        if (currentSlide >= totalSlides) {
            setCurrentSlide(Math.max(0, totalSlides - 1));
        }
    }, [companyValueCards, itemsPerSlide, currentSlide]);

    const totalSlides = Math.ceil(companyValueCards.length / itemsPerSlide);
    const startIndex = currentSlide * itemsPerSlide;
    const visibleCards = companyValueCards.slice(startIndex, startIndex + itemsPerSlide);

    const goToPreviousSlide = () =>
        setCurrentSlide((prev) => (prev - 1 + totalSlides) % totalSlides);
    const goToNextSlide = () => setCurrentSlide((prev) => (prev + 1) % totalSlides);

    return (
        <div className={'space-y-4'}>
            <div
                className={'grid grid-cols-1 items-start gap-[12px] sm:grid-cols-2 lg:grid-cols-4'}
            >
                {visibleCards.map((value, index) => (
                    <CompanyValueCard
                        key={`${value.title}-${index}`}
                        {...value}
                    />
                ))}
            </div>

            {totalSlides > 1 && (
                <div className={'flex items-center justify-between'}>
                    <div className={'flex gap-2'}>
                        <button
                            type={'button'}
                            onClick={goToPreviousSlide}
                            className={'rounded-lg border border-gray-200 p-2 hover:bg-gray-50'}
                            aria-label={t('common.previous')}
                        >
                            <Icon
                                name={'arrow-arrow-left-1'}
                                size={'sm'}
                                color={Colors.BASE_600}
                            />
                        </button>
                        <button
                            type={'button'}
                            onClick={goToNextSlide}
                            className={'rounded-lg border border-gray-200 p-2 hover:bg-gray-50'}
                            aria-label={t('common.next')}
                        >
                            <Icon
                                name={'arrow-arrow-right-1'}
                                size={'sm'}
                                color={Colors.BASE_600}
                            />
                        </button>
                    </div>
                    <div className={'flex gap-2'}>
                        {Array.from({ length: totalSlides }).map((_, index) => (
                            <button
                                key={index}
                                type={'button'}
                                onClick={() => setCurrentSlide(index)}
                                className={`h-2 rounded-full transition-all ${
                                    index === currentSlide ? 'w-6 bg-teal-500' : 'w-2 bg-gray-300'
                                }`}
                            />
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};

export const SummaryTab = ({
    cycleInstanceId,
    companyValueCards = [],
    className = '',
    dataTestId = 'summary-tab',
}: SummaryTabProps) => {
    const { t } = useAmelioTranslations();
    const { summary, generate, isGenerating, error } = useManagerAssessmentSummary(cycleInstanceId);

    // Generate when the tab opens; the backend serves an already-generated summary from cache.
    useEffect(() => {
        if (cycleInstanceId) {
            void generate();
        }
    }, [cycleInstanceId, generate]);

    const isLoading = isGenerating;
    const aiParagraph = summary?.overview ?? '';
    const strengths = summary?.strengths ?? [];
    const developmentAreas = summary?.developmentAreas ?? [];

    const hasAiContent = aiParagraph || strengths.length > 0 || developmentAreas.length > 0;

    if (isLoading) {
        return (
            <div className={`space-y-6 ${className}`}>
                <div className={'flex items-center gap-[12px] border-b border-gray-100 pb-[24px]'}>
                    <div
                        className={
                            'bg-primary/10 flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full'
                        }
                    >
                        <Icon
                            name={'lucide-sparkles'}
                            size={24}
                            strokeWidth={2}
                            color={Colors.PRIMARY}
                        />
                    </div>
                    <div>
                        <Text
                            size={'large'}
                            weight={'bold'}
                            color={Colors.NAVY}
                            className={'text-subtitle!'}
                        >
                            {t('postReviewResults.summary.aiAnalysis')}
                        </Text>
                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-caption! mt-[2px]'}
                        >
                            {t('postReviewResults.summary.aiDescription')}
                        </Text>
                    </div>
                </div>
                <Skeleton className={'space-y-6'}>
                    <Bone className={'h-16 w-full'} />
                    <div className={'space-y-3'}>
                        <Bone className={'h-5 w-30'} />
                        <Bone className={'h-12 w-full'} />
                        <Bone className={'h-12 w-full'} />
                    </div>
                </Skeleton>
            </div>
        );
    }

    if (error) {
        // assessmentNotReady is a legitimate, already-friendly status (nothing to retry: the manager
        // hasn't submitted the assessment yet). generationFailed covers everything else — the raw
        // technical detail is logged for the team (see useManagerAssessmentSummary) and never shown
        // here; a manager or HR user only ever sees this human sentence, with a way to try again (#15591).
        const isGenerationFailure = error === 'generationFailed';

        return (
            <div
                className={`space-y-6 ${className} p-5`}
                data-testid={dataTestId}
            >
                <div className={'flex items-center gap-[12px] border-b border-gray-200 pb-[24px]'}>
                    <div
                        className={
                            'flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-red-100'
                        }
                    >
                        <Icon
                            name={'essential-danger'}
                            size={24}
                            strokeWidth={2}
                            color={Colors.ERROR}
                        />
                    </div>
                    <div>
                        <Text
                            size={'large'}
                            weight={'bold'}
                            color={Colors.NAVY}
                            className={'text-subtitle!'}
                        >
                            {t('postReviewResults.summary.aiAnalysis')}
                        </Text>
                        <Text
                            size={'small'}
                            color={Colors.ERROR}
                            className={'text-caption! mt-[2px]'}
                        >
                            {isGenerationFailure
                                ? t('postReviewResults.summary.loadErrorTitle')
                                : t('postReviewResults.summary.assessmentNotReady')}
                        </Text>
                    </div>
                </div>

                {isGenerationFailure && (
                    <div
                        className={'rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center'}
                    >
                        <Text
                            size={'default'}
                            color={Colors.BASE_400}
                            className={'leading-relaxed'}
                        >
                            {t('postReviewResults.summary.loadErrorDescription')}
                        </Text>
                        <button
                            type={'button'}
                            onClick={() => void generate()}
                            disabled={isGenerating}
                            className={
                                'mt-[16px] rounded-lg border border-gray-200 bg-white px-4 py-2 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50'
                            }
                        >
                            {isGenerating
                                ? t('postReviewResults.summary.regenerating')
                                : t('postReviewResults.summary.regenerate')}
                        </button>
                    </div>
                )}
            </div>
        );
    }

    if (!hasAiContent) {
        return (
            <div
                className={`space-y-6 ${className} p-5`}
                data-testid={dataTestId}
            >
                <div className={'flex items-center gap-[12px] border-b border-gray-200 pb-[24px]'}>
                    <div
                        className={
                            'flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-gray-100'
                        }
                    >
                        <Icon
                            name={'essential-info-circle'}
                            size={24}
                            strokeWidth={2}
                            color={Colors.BASE_400}
                        />
                    </div>
                    <div>
                        <Text
                            size={'large'}
                            weight={'bold'}
                            color={Colors.NAVY}
                            className={'text-subtitle!'}
                        >
                            {t('postReviewResults.summary.aiAnalysis')}
                        </Text>
                        <Text
                            size={'small'}
                            color={Colors.BASE_400}
                            className={'text-caption! mt-[2px]'}
                        >
                            {t('postReviewResults.summary.aiDescription')}
                        </Text>
                    </div>
                </div>
                <div className={'rounded-2xl border border-gray-200 bg-gray-50 p-8 text-center'}>
                    <Text
                        size={'default'}
                        color={Colors.BASE_400}
                        className={'leading-relaxed'}
                    >
                        {t('postReviewResults.summary.noData')}
                    </Text>
                </div>
            </div>
        );
    }

    return (
        <div
            className={`space-y-6 ${className} p-2 sm:p-5`}
            data-testid={dataTestId}
        >
            {/* AI Header */}
            <div className={'flex items-center gap-[12px] border-b border-gray-200 pb-[24px]'}>
                <div
                    className={
                        'flex h-[48px] w-[48px] shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-purple-500 to-blue-500 shadow-lg'
                    }
                >
                    <Icon
                        name={'lucide-sparkles'}
                        size={24}
                        strokeWidth={2}
                        color={Colors.WHITE}
                    />
                </div>
                <div>
                    <Text
                        size={'large'}
                        weight={'bold'}
                        color={Colors.NAVY}
                        className={'text-subtitle!'}
                    >
                        {t('postReviewResults.summary.aiAnalysis')}
                    </Text>
                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'text-caption! mt-[2px]'}
                    >
                        {t('postReviewResults.summary.aiDescription')}
                    </Text>
                </div>
            </div>

            {/* AI paragraph */}
            <div
                className={
                    'rounded-[16px] border border-purple-100 bg-gradient-to-r from-purple-50 via-blue-50 to-cyan-50 p-[24px]'
                }
            >
                <Text
                    size={'default'}
                    color={Colors.NAVY}
                    className={'text-body-sm! leading-relaxed'}
                >
                    {aiParagraph}
                </Text>
            </div>

            {/* Strengths */}
            <SummarySection
                title={t('postReviewResults.summary.strengths')}
                items={strengths}
                dotColor={'bg-primary'}
                emptyState={{
                    variant: 'strength',
                    title: t('postReviewResults.summary.noStrengthsTitle'),
                    description: t('postReviewResults.summary.noStrengthsDescription'),
                }}
            />

            {/* Development Areas */}
            <SummarySection
                title={t('postReviewResults.summary.developmentAreas')}
                items={developmentAreas}
                dotColor={'bg-warning'}
                emptyState={{
                    variant: 'development',
                    title: t('postReviewResults.summary.noDevelopmentAreasTitle'),
                    description: t('postReviewResults.summary.noDevelopmentAreasDescription'),
                }}
            />

            {/* Alignment with Company Values */}
            {companyValueCards.length > 0 && (
                <div className={'mt-[40px]'}>
                    <Text
                        size={'large'}
                        weight={'bold'}
                        color={Colors.NAVY}
                        className={'mb-[4px] leading-normal'}
                    >
                        {t('postReviewPrepareMeeting.companyValues.title')}
                    </Text>
                    <Text
                        size={'small'}
                        color={Colors.BASE_400}
                        className={'mb-[16px] leading-normal'}
                    >
                        {t('postReviewPrepareMeeting.companyValues.subtitle')}
                    </Text>

                    <CompanyValuesCarousel companyValueCards={companyValueCards} />
                </div>
            )}
        </div>
    );
};

const EMPTY_STATE_BADGE = {
    strength: 'bg-primary/10 text-primary',
    development: 'bg-warning/10 text-warning',
} as const;

const SummaryEmptyState = ({ variant, title, description }: SummaryEmptyStateProps) => (
    <div
        className={'flex flex-col items-center py-[16px] text-center'}
        data-testid={`summary-empty-${variant}`}
    >
        <div
            className={`mb-[16px] flex h-[52px] w-[52px] items-center justify-center rounded-full ${EMPTY_STATE_BADGE[variant]}`}
            aria-hidden={'true'}
        >
            <svg
                viewBox={'0 0 24 24'}
                className={'h-[24px] w-[24px]'}
                fill={'none'}
                stroke={'currentColor'}
                strokeWidth={1.8}
                strokeLinecap={'round'}
                strokeLinejoin={'round'}
            >
                {variant === 'strength' ? (
                    <>
                        <circle
                            cx={'12'}
                            cy={'12'}
                            r={'8'}
                        />
                        <circle
                            cx={'12'}
                            cy={'12'}
                            r={'4.2'}
                        />
                        <circle
                            cx={'12'}
                            cy={'12'}
                            r={'.6'}
                            fill={'currentColor'}
                            stroke={'none'}
                        />
                    </>
                ) : (
                    <>
                        <circle
                            cx={'12'}
                            cy={'12'}
                            r={'9'}
                        />
                        <path d={'M8.3 12.4l2.6 2.6 5-5.2'} />
                    </>
                )}
            </svg>
        </div>
        <Text
            size={'default'}
            weight={'semibold'}
            color={Colors.NAVY}
            className={'mb-[8px] w-full text-center'}
        >
            {title}
        </Text>
        <Text
            size={'small'}
            color={Colors.BASE_400}
            className={'mx-auto w-full max-w-[42ch] text-center leading-relaxed'}
        >
            {description}
        </Text>
    </div>
);

const SummarySection = ({ title, items, dotColor, emptyState }: SummarySectionProps) => {
    return (
        <div className={'mt-[40px]'}>
            <Text
                size={'large'}
                weight={'bold'}
                color={Colors.NAVY}
                className={'text-body-lg! mb-[8px]'}
            >
                {title}
            </Text>

            {items.length === 0 ? (
                <SummaryEmptyState {...emptyState} />
            ) : (
                <div className={'grid gap-[16px]'}>
                    {items.map((item, index) => (
                        <div
                            key={index}
                            className={`flex gap-[16px] ${
                                index < items.length - 1 ? 'border-b border-gray-200 pb-[16px]' : ''
                            }`}
                        >
                            <span
                                className={`mt-[8px] h-[8px] w-[8px] shrink-0 rounded-full ${dotColor}`}
                            />
                            <div>
                                <Text
                                    size={'small'}
                                    weight={'semibold'}
                                    color={Colors.NAVY}
                                    className={'text-body-sm! mb-[4px]'}
                                >
                                    {item.title}
                                </Text>
                                <Text
                                    size={'small'}
                                    color={Colors.BASE_400}
                                    className={'text-body-sm! leading-relaxed'}
                                >
                                    {item.description}
                                </Text>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default SummaryTab;
